import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  Download,
  FileText,
  ListChecks,
  ScanLine,
  Smartphone,
  Upload,
  WandSparkles,
  WalletCards,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isAndroidWeb, setIsAndroidWeb] = useState(false);

  useEffect(() => {
    const isAndroid = /Android/i.test(window.navigator.userAgent);
    const isNativeApp = window.Capacitor?.isNativePlatform?.() === true;
    setIsAndroidWeb(isAndroid && !isNativeApp);
  }, []);

  useEffect(() => {
    const fetchDocuments = async () => {
      setLoading(true);
      setError(false);
      try {
        const { data } = await api.get('/documents');
        setDocuments(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(true);
        console.error('Failed to fetch documents:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDocuments();
  }, []);

  const stats = useMemo(() => {
    const completed = documents.filter((doc) => doc.status === 'done').length;
    const active = documents.filter((doc) => ['pending', 'assigned', 'typing', 'review', 'awaiting_payment'].includes(doc.status)).length;
    return { completed, active };
  }, [documents]);

  const remaining = user?.free_scans_remaining ?? user?.credits_balance ?? 0;
  const recentDocuments = documents.slice(0, 4);
  const firstName = user?.name?.trim()?.split(/\s+/)[0] || 'there';

  const actions = [
    {
      title: 'Upload Document',
      description: 'Scan a handwritten page or document and start processing.',
      icon: Upload,
      accent: 'bg-blue-50 text-blue-700 border-blue-100',
      onClick: () => navigate('/upload'),
    },
    {
      title: 'Track Progress',
      description: 'Follow active jobs from upload through review and delivery.',
      icon: ListChecks,
      accent: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      onClick: () => navigate('/tasks'),
    },
    {
      title: 'Enhance & Repair',
      description: 'Correct skew, glare, fading, and noise before transcription.',
      icon: WandSparkles,
      accent: 'bg-violet-50 text-violet-700 border-violet-100',
      onClick: () => navigate('/upload?enhance=true'),
    },
    {
      title: user?.is_tester ? 'Tester Access' : 'Buy Credits',
      description: user?.is_tester
        ? 'Your tester account has unlimited document processing.'
        : 'Add scan credits securely through Stripe Checkout.',
      icon: WalletCards,
      accent: 'bg-amber-50 text-amber-700 border-amber-100',
      onClick: () => {
        if (!user?.is_tester) window.dispatchEvent(new Event('wallet:open'));
      },
    },
  ];

  return (
    <main className="min-h-screen bg-slate-50 pb-28" data-testid="home-page">
      <section className="bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 text-white">
        <div className="mx-auto max-w-5xl px-4 pb-10 pt-24 sm:px-6 sm:pb-12 sm:pt-20">
          <p className="text-sm font-semibold text-blue-100">Welcome back, {firstName}</p>
          <h1 className="mt-2 max-w-2xl text-3xl font-bold tracking-tight sm:text-4xl">
            What would you like to accomplish today?
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-blue-100 sm:text-base">
            Upload documents, monitor transcription progress, and manage your available scans from one place.
          </p>

          <div className="mt-7 grid grid-cols-2 gap-3 sm:max-w-xl sm:grid-cols-4">
            <div className="rounded-xl border border-white/20 bg-white/10 p-3 backdrop-blur-sm">
              <ScanLine size={18} className="text-blue-100" />
              <strong className="mt-2 block text-xl">{user?.is_tester ? '∞' : remaining}</strong>
              <span className="text-xs text-blue-100">Remaining scans</span>
            </div>
            <div className="rounded-xl border border-white/20 bg-white/10 p-3 backdrop-blur-sm">
              <WalletCards size={18} className="text-blue-100" />
              <strong className="mt-2 block text-xl">{user?.is_tester ? 'Unlimited' : `${remaining} credits`}</strong>
              <span className="text-xs text-blue-100">Wallet balance</span>
            </div>
            <div className="rounded-xl border border-white/20 bg-white/10 p-3 backdrop-blur-sm">
              <Clock3 size={18} className="text-blue-100" />
              <strong className="mt-2 block text-xl">{stats.active}</strong>
              <span className="text-xs text-blue-100">Active jobs</span>
            </div>
            <div className="rounded-xl border border-white/20 bg-white/10 p-3 backdrop-blur-sm">
              <CheckCircle2 size={18} className="text-blue-100" />
              <strong className="mt-2 block text-xl">{stats.completed}</strong>
              <span className="text-xs text-blue-100">Completed</span>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 sm:px-6">
        <section aria-labelledby="quick-actions-heading">
          <div className="mb-4">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">Quick actions</p>
            <h2 id="quick-actions-heading" className="mt-1 text-xl font-bold text-slate-900">Start here</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {actions.map(({ title, description, icon: Icon, accent, onClick }) => (
              <button
                key={title}
                type="button"
                onClick={onClick}
                className="group rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <span className={`inline-flex rounded-xl border p-3 ${accent}`}><Icon size={23} /></span>
                <strong className="mt-4 block text-base text-slate-900">{title}</strong>
                <span className="mt-1 block min-h-10 text-sm leading-5 text-slate-600">{description}</span>
                <span className="mt-4 flex items-center gap-1 text-sm font-semibold text-blue-700">
                  Open <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </span>
              </button>
            ))}
          </div>
        </section>

        {isAndroidWeb && (
          <section className="flex flex-col gap-4 rounded-2xl border border-indigo-200 bg-indigo-50 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="home-android-download">
            <div className="flex gap-3">
              <span className="rounded-xl bg-indigo-100 p-3 text-indigo-700"><Smartphone size={24} /></span>
              <div>
                <h2 className="font-bold text-slate-900">Take GetScribeDash with you</h2>
                <p className="mt-1 text-sm text-slate-600">Install the Android app for fast uploads from your phone.</p>
              </div>
            </div>
            <a href="/downloads/getscribedash.apk" download="getscribedash.apk" className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-700 px-4 py-3 text-sm font-semibold text-white">
              <Download size={17} /> Install Android App (.apk)
            </a>
          </section>
        )}

        <section aria-labelledby="recent-work-heading">
          <div className="mb-4 flex items-end justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">Documents</p>
              <h2 id="recent-work-heading" className="mt-1 text-xl font-bold text-slate-900">Recent and available work</h2>
            </div>
            <button type="button" onClick={() => navigate('/tasks')} className="text-sm font-semibold text-blue-700">View all</button>
          </div>

          {error ? (
            <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
              Documents could not load. Your session remains active; try opening Tasks again shortly.
            </div>
          ) : loading ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Loading your workspace…</div>
          ) : recentDocuments.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
              <FileText size={30} className="mx-auto text-slate-300" />
              <h3 className="mt-3 font-semibold text-slate-900">No documents yet</h3>
              <p className="mt-1 text-sm text-slate-500">Upload your first page to start building your dashboard.</p>
              <button type="button" onClick={() => navigate('/upload')} className="mt-4 rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white">Upload a document</button>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {recentDocuments.map((doc) => (
                <button
                  key={doc.id || doc._id}
                  type="button"
                  onClick={() => navigate(`/document/${doc.id}`)}
                  className="flex items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-blue-200 hover:shadow-md"
                >
                  <span className="min-w-0">
                    <strong className="block truncate text-sm text-slate-900">{doc.title || 'Untitled Document'}</strong>
                    <span className="mt-1 block line-clamp-2 text-xs leading-5 text-slate-500">{doc.description || 'Open to view document details and progress.'}</span>
                  </span>
                  <span className="shrink-0 rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-semibold capitalize text-slate-600">{doc.status || 'pending'}</span>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
